/**
 * Vesper Floating Upload Indicator
 *
 * Shows upload progress as a floating pill that persists across navigation.
 * Positioned fixed above the bottom nav bar.
 *
 * States:
 *  - Compressing: pulsing icon + "Compressing..."
 *  - Uploading: progress ring + percentage
 *  - Complete: checkmark, auto-dismiss after 3s
 *  - Error: red indicator with retry text
 */

import React, { useEffect, useRef } from 'react';
import { useUploadStore } from '@/stores/uploadStore';
import { Check, AlertCircle, Loader2, Upload } from 'lucide-react';

/** SVG progress ring — 36px diameter, 3px stroke */
function ProgressRing({ progress }: { progress: number }) {
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width="36" height="36" className="rotate-[-90deg]">
      {/* Background ring */}
      <circle
        cx="18" cy="18" r={radius}
        fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3"
      />
      {/* Progress ring */}
      <circle
        cx="18" cy="18" r={radius}
        fill="none" stroke="white" strokeWidth="3"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-300 ease-out"
      />
    </svg>
  );
}

export default function UploadIndicator(): React.ReactElement | null {
  const activeUpload = useUploadStore((s) => s.activeUpload);
  const clearUpload = useUploadStore((s) => s.clearUpload);
  const retryUpload = useUploadStore((s) => s.retryUpload);
  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss on completion
  useEffect(() => {
    if (activeUpload?.status === 'complete') {
      autoDismissRef.current = setTimeout(() => {
        clearUpload();
      }, 3000);
    }
    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current);
    };
  }, [activeUpload?.status, clearUpload]);

  if (!activeUpload) return null;

  const { status, progress, error, retryCount } = activeUpload;
  const maxRetries = 3;
  const canRetry = retryCount < maxRetries;

  return (
    <div
      className="fixed bottom-24 right-4 z-[9990]"
      style={{ animation: 'uploadSlideIn 0.3s ease-out' }}
    >
      <style>{`
        @keyframes uploadSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        className={`flex items-center gap-2.5 rounded-full px-4 py-2.5 shadow-xl backdrop-blur-md border transition-colors duration-300 ${
          status === 'error'
            ? 'bg-white/5 border-white/10'
            : status === 'complete'
              ? 'bg-green-500/80 border-green-400/30'
              : 'bg-black/70 border-white/10'
        }`}
      >
        {/* Icon / Progress */}
        {status === 'compressing' && (
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        )}
        {status === 'uploading' && (
          <div className="relative w-9 h-9 flex items-center justify-center">
            <ProgressRing progress={progress} />
            <Upload className="w-3.5 h-3.5 text-white absolute" />
          </div>
        )}
        {status === 'complete' && (
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
        {status === 'error' && (
          <AlertCircle className="w-5 h-5 text-red-400" />
        )}

        {/* Label */}
        <span className="text-white text-xs font-medium whitespace-nowrap">
          {status === 'compressing' && 'Compressing...'}
          {status === 'uploading' && `${Math.round(progress)}%`}
          {status === 'complete' && 'Posted!'}
          {status === 'error' && (canRetry ? (error || 'Upload failed') : 'Upload failed \u2014 please try again')}
        </span>

        {/* Retry + Dismiss buttons for errors */}
        {status === 'error' && canRetry && (
          <button
            onClick={() => void retryUpload()}
            className="px-2.5 py-1 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-[10px] font-medium ml-1 transition-colors"
          >
            Retry ({retryCount + 1}/{maxRetries})
          </button>
        )}
        {status === 'error' && (
          <button
            onClick={clearUpload}
            className="text-white/70 hover:text-white text-[10px] underline ml-1"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
